import * as React from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo } from 'react';
import { camelCase } from 'lodash';
import type { VirtualizedItemListHandle } from './VirtualizedItemList';
import { GROUP_SEPARATOR, type GeneralConfig } from './useDocumentConfig';
import { interactionStore } from './interactionStore';

interface AssistanceViewContextProviderProps {
    onUpdate: (field: IEventField & { action?: string; payload?: any }) => Promise<any>;
    // may return void: degenerate reselections (point-only bbox) are ignored
    onReselect: (field: IEventField & { bbox: any; pageIndex: number }) => Promise<any> | void;
    // event hook fired with the target field before an update/action/reselect mutation is
    // sent — the interaction state itself lives in the interaction store; this exists for
    // side effects like list_of_services' cross-chapter loading
    onBeforeFieldUpdate?: (field: IEventField) => void;
    onFastTrackChange?: (isFastTrackEnabled: boolean) => void;
    isFastTrackEnabled?: boolean;
    generalConfig?: GeneralConfig;
    formsRef?: React.RefObject<HTMLDivElement>;
    previewRef?: React.RefObject<HTMLDivElement>;
    itemVirtualizerRef?: React.RefObject<VirtualizedItemListHandle>;
}

interface AssistanceViewContextProps {
    onFieldUpdate: (field: IEventField & { payload: any }) => Promise<any>;
    onFieldAction: (field: IEventField & { action: string; payload?: any }) => Promise<any>;
    onFieldReselect: (field: IEventField & { bbox: any; pageIndex: number; isEmailOcr?: boolean }) => Promise<any>;
    onFieldReselectStart: (field: IEventField) => void;
    onFieldReselectEnd: (field: IEventField) => void;
    // marks the field as updating until `pending` settles, for requests that run before its own update
    onFieldPending: (field: IEventField, pending: Promise<any>) => void;
    onFieldFocus: (field: IEventField) => void;
    onFieldBlur: (field: IEventField) => void;
    onFieldMouseEnter: (field: IEventField) => void;
    onFieldMouseLeave: (field: IEventField) => void;
    onItemMouseEnter: (itemIndex: number) => void;
    onItemMouseLeave: (itemIndex: number) => void;
    onFastTrackChange?: (isFastTrackEnabled: boolean) => void;
    isFastTrackEnabled?: boolean;
    generalConfig?: GeneralConfig;
    formsRef?: React.RefObject<HTMLDivElement>;
    previewRef?: React.RefObject<HTMLDivElement>;
    itemVirtualizerRef?: React.RefObject<VirtualizedItemListHandle>;
}

const AssistanceViewContext = createContext<AssistanceViewContextProps>({} as AssistanceViewContextProps);

export interface IEventField {
    // locator
    fieldName?: string;
    itemIndex?: number;
    itemType?: string;
    // view control
    reselectTargetRef?: React.RefObject<HTMLElement>;
    // the reselect target handles a selection that leaves the page the drag started on
    canSelectAcrossPages?: boolean;
}

export const AssistanceViewContextProvider = ({
    onUpdate,
    onReselect,
    onBeforeFieldUpdate,
    onFastTrackChange,
    isFastTrackEnabled,
    generalConfig,
    children,
    formsRef,
    previewRef,
    itemVirtualizerRef,
}: AssistanceViewContextProviderProps & { children: any }) => {
    // the provider owns the interaction store: every field event writes the store, which is
    // the single subscribable source of truth for focus/hover/update/reselect state
    useEffect(() => () => interactionStore.reset(), []);

    const handleFieldUpdate = useCallback(
        ({ payload, ...field }: { payload: any } & IEventField) => {
            interactionStore.setState({ updateField: field });
            onBeforeFieldUpdate?.(field);
            return Promise.resolve(onUpdate({ payload, ...field })).finally(() => {
                interactionStore.setState({ updateField: null });
            });
        },
        [onUpdate]
    );

    const handleFieldAction = useCallback(
        ({
            action,
            payload,
            ...field
        }: {
            action: string;
            payload?: any;
        } & IEventField) => {
            interactionStore.setState({ updateField: field });
            onBeforeFieldUpdate?.(field);
            return Promise.resolve(onUpdate({ ...field, action, payload })).finally(() => {
                interactionStore.setState({ updateField: null });
            });
        },
        [onUpdate]
    );

    const handleFieldReselectStart = useCallback((field: IEventField) => {
        interactionStore.setState({ reselectField: field });
    }, []);

    const handleFieldReselectEnd = useCallback((field: IEventField) => {
        interactionStore.setState({ reselectField: null });
    }, []);

    const handleFieldPending = useCallback((field: IEventField, pending: Promise<any>) => {
        interactionStore.setState({ updateField: field });
        pending.finally(() => {
            interactionStore.setState({ updateField: null });
        });
    }, []);

    const handleFieldReselect = useCallback(
        ({
            bbox,
            pageIndex,
            ...field
        }: {
            bbox: any;
            pageIndex: number;
        } & IEventField) => {
            const reselectFieldName = field?.fieldName?.split(GROUP_SEPARATOR).map(camelCase).join('.');

            interactionStore.setState({ updateField: field });
            onBeforeFieldUpdate?.(field);
            return Promise.resolve(onReselect({ ...field, fieldName: reselectFieldName, bbox, pageIndex })).finally(
                () => {
                    interactionStore.setState({ updateField: null });
                }
            );
        },
        [onReselect]
    );

    const handleFieldFocus = useCallback((field: IEventField) => {
        interactionStore.setState({ focusField: field });
    }, []);

    const handleFieldBlur = useCallback((field: IEventField) => {
        interactionStore.setState({ focusField: null });
    }, []);

    const handleFieldMouseEnter = useCallback((field: IEventField) => {
        interactionStore.setState({ hoverField: field });
    }, []);

    const handleFieldMouseLeave = useCallback((field: IEventField) => {
        interactionStore.setState({ hoverField: null });
    }, []);

    const handleItemMouseEnter = useCallback((itemIndex: number) => {
        interactionStore.setState({ hoverItemIndex: itemIndex });
    }, []);

    const handleItemMouseLeave = useCallback((itemIndex: number) => {
        interactionStore.setState({ hoverItemIndex: null });
    }, []);

    const contextValue = useMemo(
        () => ({
            onFieldUpdate: handleFieldUpdate,
            onFieldAction: handleFieldAction,
            onFieldReselect: handleFieldReselect,
            onFieldReselectStart: handleFieldReselectStart,
            onFieldReselectEnd: handleFieldReselectEnd,
            onFieldPending: handleFieldPending,
            onFieldFocus: handleFieldFocus,
            onFieldBlur: handleFieldBlur,
            onFieldMouseEnter: handleFieldMouseEnter,
            onFieldMouseLeave: handleFieldMouseLeave,
            onItemMouseEnter: handleItemMouseEnter,
            onItemMouseLeave: handleItemMouseLeave,
            onFastTrackChange,
            isFastTrackEnabled,
            generalConfig,
            formsRef,
            previewRef,
            itemVirtualizerRef,
        }),
        [
            handleFieldUpdate,
            handleFieldAction,
            handleFieldReselect,
            handleFieldReselectStart,
            handleFieldReselectEnd,
            handleFieldPending,
            handleFieldFocus,
            handleFieldBlur,
            handleFieldMouseEnter,
            handleFieldMouseLeave,
            handleItemMouseEnter,
            handleItemMouseLeave,
            onFastTrackChange,
            isFastTrackEnabled,
            generalConfig,
            formsRef,
            previewRef,
            itemVirtualizerRef,
        ]
    );

    return <AssistanceViewContext.Provider value={contextValue}>{children}</AssistanceViewContext.Provider>;
};

export const useAssistanceViewContext = () => useContext(AssistanceViewContext);
